import { Alert, Button, Form, FormGroup, Input, Label, Spinner } from 'reactstrap';
import styles from './LoginPage.module.scss';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect, useContext } from 'react';
import { useOnChangeHandler } from '../../global/utils/hooks/useOnChangeHandler';
import { useToggle } from '../../global/utils/hooks/useToggle';
import { postRequest } from '../../global/utils/apiCall';
import useAuth from '../../global/utils/hooks/useAuth';
import { AppContext, UserContext } from '../../global/globalContext/context';
import { showMsg } from '../../global/langs/langs';
import SEO from '../../global/Components/SEO/SEO';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const LOGIN_URL = 'api/v1/user/signin';

export default function LoginPage() {
  const { auth,setAuth, persist, setPersist } = useAuth();
  const {lang} = useContext(UserContext);
  const { setting } = useContext(AppContext);
  const { executeRecaptcha  } = useGoogleReCaptcha();


  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard/user-profile";

  const loginRef = useRef();
  const errorRef = useRef();


  const [login, setLogin, onChangeLogin] = useOnChangeHandler('');
  const [password, setPassword, onChangePassword] = useOnChangeHandler('');

  const [ errorMsg, setErrorMsg] = useState('');

  const [isShowPassword, setIsShoPassword, toggleIsShowPassword] = useToggle(false);
  const [ isLoading, setIsLoading ] = useState(false);

  useEffect(() => {
    if(auth?.token) {
      navigate('/dashboard/user-profile');
    }
    
    loginRef.current.focus();
  },[]);
  
  useEffect(() => {
    setErrorMsg('');
  },[login,password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
   setIsLoading(true);


   if (!executeRecaptcha) {
    setErrorMsg('Execute recaptcha not yet available');

    return;
  }
   
    try {
      let recaptchaToken;

      try{
        recaptchaToken = await executeRecaptcha('login');
      }
      catch (recaptchaError) {
        setErrorMsg('Recaptcha failed: ' + recaptchaError.message);
        return
      }

      const response = await postRequest(LOGIN_URL, JSON.stringify({login, password, remember: persist, 'g-recaptcha-response': recaptchaToken}));
     
      const userInfo = response?.data?.data;
      const token = response?.data?.data?.access_token;
      const roles = response?.data?.data?.user?.is_seller;
      const user = response?.data?.data?.user?.username;
      setAuth({user, roles, token, userInfo});
      localStorage.setItem('auth', JSON.stringify({user, roles, token, userInfo}));
      setLogin('');
      setPassword('');
      setIsLoading(false);
      navigate(from, {replace: true});
    }
    catch (error) {
      if (!error?.response) {
        setErrorMsg(showMsg('no_server_response',lang));
      } else if (error.response?.status === 400) {
        setErrorMsg(showMsg('bad_request', lang));
      } else if (error.response?.status === 401) {
        setErrorMsg(showMsg('unauthorized', lang));
      } else if (error.response?.status === 422){
        setErrorMsg(error?.response?.data?.message);
      } else {
        setErrorMsg(showMsg('login_failed', lang))
      }
      errorRef.current.focus();
    }
    finally{
     setIsLoading(false);
    }
  }

  const handleTogglePersist = () => {
    setPersist(prev => !prev)
  }

  useEffect(() => {
    localStorage.setItem("persist", persist);
  },[persist])

  return (
    <>
      <SEO title={'Login'} />
      <div className={`d-flex align-items-center justify-content-center ${styles.loginContainer}`}>
        <div className={`${styles.login} lgtBackground bg-white p-4`}>
          <Alert aria-live="assertive"
              color='danger'
              innerRef={errorRef}
              className={errorMsg ? `` : `d-none`}
              >
                {errorMsg}
          </Alert>
          <Link to='/' className=''>
            {
              setting
                ? <img src={`${`${process.env.REACT_APP_API_URL}${setting.logo
                }`}`} alt="letgo or trade logo" width={'120px'} className={`my-3`}/>
                : ''
            }
          </Link>
          <div className='fw-bold mb-3 '>
            {showMsg('login', lang)}
          </div>
          <Form className='mb-3' onSubmit={handleSubmit}>
            <FormGroup floating>
              <Input 
                id="1"
                type='text'
                value={login}
                placeholder='Email or username'
                innerRef={loginRef}
                onChange={onChangeLogin}
                required
                />
              <Label>
                {showMsg('email_or_login', lang)}
              </Label>
            </FormGroup>
            <FormGroup floating>
              <Input 
                id="2"
                value={password}
                className={`${styles.showPassword}`}
                type = { !isShowPassword ? "password" : "text"}
                placeholder='Password'
                onChange ={onChangePassword}
                required
                />
                <span className={`${styles.showIcon}`}
                      onClick={toggleIsShowPassword}
                      >
                  <i className={`bi ${!isShowPassword ? "bi-eye" : "bi-eye-slash"}`}></i>
                </span>
              <Label>
                {showMsg('password', lang)}
              </Label>
            </FormGroup>
            <Button 
              block 
              className={`general-button fw-semibold largBtn mb-3`}
              disabled={!login || !password || isLoading ? true : false}
            >
              {
                isLoading 
                  ? (
                    <>
                      <Spinner size="sm">
                        Loading...
                      </Spinner>
                      <span>
                        {' '}Loading
                      </span>
                    </>
                  )
                  : showMsg('login', lang)
              }
            </Button>
            <FormGroup check>
              <Input 
                type="checkbox"
                onChange={handleTogglePersist}
                checked={persist}
                        
              />
              <Label check>
                {showMsg('remember_me', lang)}
              </Label>
            </FormGroup>
          </Form>
          <div className={`fw-semibold text-center mb-2`}>
            <span>{showMsg('password_lost?', lang)}{' '}</span>
            <Link to='/forgot-password' className='text-decoration-none'>{showMsg('forgot-password', lang)}</Link>
          </div>
          <div className={`fw-semibold text-center`}>
            <span>{showMsg('already_registered?', lang)}{' '}</span>
            <Link to='/register' className='text-decoration-none'>{showMsg('create_an_account', lang)}</Link>
          </div>
        </div>
      </div>
    </>
  )
}
